import { eCommerceClient } from 'axios/client';
import Error404 from 'components/desktop/error-404';
import MobileError404 from 'components/mobile/error-404';
import ECommerceCartProvider from 'components/providers/e-commerce-cart-provider';
import {
  DEFAULT_DESCRIPTION,
  DEFAULT_OG_IMAGE,
  DEFAULT_TITLE,
  E_COMMERCE_STORAGE_URL,
  MAIN_DOMAIN,
  SITE_MAP,
} from 'globalConstants';
import { Omit, find, isEmpty, isEqual, split, toString } from 'lodash';
import type { GetServerSideProps, NextPage } from 'next';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { Context, Fragment, createContext } from 'react';
import {
  CategoriesParams,
  CategoriesResponse,
  CategoryParams,
  CategoryResponse,
  CategorySerialization,
} from 'restful/e-commerce';
import { detectDeviceByUserAgent } from 'utils';

const MobileECommerceListProduct = dynamic(
  () => import('components/mobile/e-commerce-list-product'),
);
const ECommerceListProduct = dynamic(() => import('components/desktop/e-commerce-list-product'));

export enum FilterId {
  PRICE_RANGE = '1',
}
export const DEFAULT_SKELETON_CATEGORY_ITEM = 4;
export const DEFAULT_SKELETON_FILTER_ITEM = 3;
export const DEFAULT_LIMIT_PRODUCT_CARD_ITEM = 16;
export const DEFAULT_SKELETON_PRODUCT_CARD_ITEM = 16;

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { req } = context;
  const { headers } = req;
  const device = detectDeviceByUserAgent(headers['user-agent']);
  let pageData = null;
  const { [SITE_MAP.E_COMMERCE.PRODUCT.LIST_PRODUCT]: listECommerceProductPathname } =
    context.query ?? {};
  try {
    const categoryResponse = await eCommerceClient.get<CategoryParams, CategoryResponse>(
      `/v1/categories/${listECommerceProductPathname}`,
    );
    const { data: category } = categoryResponse.data ?? {};
    if (!isEmpty(category)) {
      const { data: rootCategoriesData } = await eCommerceClient.get<
        CategoriesParams,
        CategoriesResponse
      >(`/v1/categories`);
      const { data: rootCategories } = rootCategoriesData ?? {};
      const rootCategory = find(rootCategories, (rootCategory) =>
        isEqual(toString(rootCategory.category_id), split(category?.id_path, '/')[0]),
      );

      pageData = {
        category: !isEmpty(category) ? category : null,
        rootCategory: rootCategory ?? null,
      };
    }
  } catch (error) {
    console.log(error);
  }

  return {
    props: {
      device,
      pageData,
    },
  };
};

export const ECommerceListProductPageContext: Context<Omit<Props, 'device'>> = createContext({});

export interface ECommerceListProductPageData {
  category?: CategorySerialization | null;
  rootCategory?: CategorySerialization | null;
}
interface Props {
  device?: 'mobile' | 'desktop';
  pageData?: ECommerceListProductPageData | null;
}

const ECommerceListProductPage: NextPage = (props: Props) => {
  const { device, ...restProps } = props;
  const { pageData } = restProps;
  const { category, rootCategory } = pageData ?? {};
  const { category: categoryName } = category ?? {};
  const { main_pair, category_id } = rootCategory ?? {};
  const router = useRouter();
  const { [SITE_MAP.E_COMMERCE.PRODUCT.LIST_PRODUCT]: slug } = router.query ?? {};
  const title = categoryName
    ? `${categoryName} - Thương mại điện tử | ${DEFAULT_TITLE}`
    : `Thương mại điện tử | ${DEFAULT_TITLE}`;
  const description = DEFAULT_DESCRIPTION;
  const ogUrl = `${MAIN_DOMAIN}/${SITE_MAP.E_COMMERCE.INDEX}/${SITE_MAP.E_COMMERCE.PRODUCT.INDEX}/${SITE_MAP.E_COMMERCE.PRODUCT.LIST_PRODUCT}/${slug}`;
  const ogImage =
    main_pair && category_id && main_pair?.[category_id]?.image_path
      ? `${E_COMMERCE_STORAGE_URL}/images/${main_pair[category_id]?.image_path}`
      : DEFAULT_OG_IMAGE;

  return (
    <Fragment>
      <Head>
        <meta
          name='viewport'
          content='width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover'
        />
        <meta
          name='viewport'
          content='width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no'
        />
        <meta name='description' content={description} />
        <meta name='og:title' content={title} />
        <meta name='og:description' content={description} />
        <meta name='og:url' content={ogUrl} />
        <meta name='og:image' content={ogImage} />
        <title>{title}</title>
      </Head>
      <ECommerceListProductPageContext.Provider value={restProps}>
        <ECommerceCartProvider>
          {isEqual(device, 'mobile') ? (
            !isEmpty(category) ? (
              <MobileECommerceListProduct />
            ) : (
              <MobileError404 />
            )
          ) : isEqual(device, 'desktop') ? (
            !isEmpty(category) ? (
              <ECommerceListProduct />
            ) : (
              <Error404 />
            )
          ) : null}
        </ECommerceCartProvider>
      </ECommerceListProductPageContext.Provider>
    </Fragment>
  );
};

export default ECommerceListProductPage;
